import { Button } from 'antd'
import { Observer, observer, useLocalObservable } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { TbCopy } from 'react-icons/tb'

import { Form, FormField } from '@/Core/UI/Form'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { FormRow, FormSection } from '@/Ship/Components/Form/FormSection'
import { PasswordRules, type PasswordRule } from '@/Ship/Components/Form/PasswordRules'
import { cx } from '@/Ship/Helpers/cssClass'
import { generatePassword } from '@/Ship/Helpers/generatePassword'
import { PASSWORD_MIN_LENGTH_LABEL, checkPassword } from '@/Ship/Helpers/passwordRules'
import { useNotification } from '@/Ship/Hooks/useNotification'

import { ChangePasswordStore } from '../Stores/ChangePasswordStore'

import styles from './ChangePasswordTab.module.css'

/** Підказки менеджерам паролів не пропонувати збереження: пароль чужий, не поточного користувача */
const PASSWORD_INPUT_PROPS = {
    autoComplete: 'new-password',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
}

interface Props {
    /** UUID/ID користувача, якому змінюється пароль */
    userId: string | undefined | null
}

/**
 * Вкладка «Змінити пароль» у картці співробітника (і в старому профілі): два поля,
 * вимоги з позначками, «Згенерувати» підставляє випадковий пароль і показує його відкритим
 * текстом з копіюванням — адміністратор має передати його співробітнику. Працює і в
 * перегляді, і в редагуванні: зміна пароля — окремий запит, не збереження форми.
 */
export const ChangePasswordTab = observer(({ userId }: Props) => {
    const [store] = useState(() => new ChangePasswordStore())
    const { showSuccess, showError } = useNotification()
    /**
     * Останній згенерований пароль; показуємо, поки він у полі або щойно збережений.
     * Observable, не useState, і читається лише в <Observer>: Form ядра на кожен свій
     * перерендер перемонтовує вміст (UIManager.render створює новий observer-компонент),
     * тож перерендер цього компонента під час введення скидав би фокус поля.
     */
    const shown = useLocalObservable(() => ({
        generated: null as string | null,
        saved: null as string | null,
        set(generated: string | null, saved: string | null) {
            this.generated = generated
            this.saved = saved
        },
        clearSaved() {
            this.saved = null
        },
    }))

    const formContext = useMemo(
        () =>
            createFormContext({
                fields: {
                    newPassword: {
                        type: 'password',
                        label: 'Новий пароль',
                        isRequired: true,
                        inputProps: { placeholder: 'Введіть новий пароль', ...PASSWORD_INPUT_PROPS },
                    },
                    confirmPassword: {
                        type: 'password',
                        label: 'Підтвердіть новий пароль',
                        isRequired: true,
                        inputProps: { placeholder: 'Введіть новий пароль ще раз', ...PASSWORD_INPUT_PROPS },
                    },
                },
                initialValues: { newPassword: '', confirmPassword: '' },
                onSubmit: async ({ values }: any) => {
                    store.setNewPassword(values['newPassword'] ?? '')
                    store.setConfirmPassword(values['confirmPassword'] ?? '')
                    const ok = await store.submit(userId, { success: showSuccess, error: showError })
                    if (!ok) return
                    const generated = shown.generated
                    shown.set(generated, generated !== null && values['newPassword'] === generated ? generated : null)
                    formContext.reset()
                },
                // Ручне введення після збереження: «Збережений пароль» більше не актуальний
                onChange: () => shown.clearSaved(),
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userId]
    )

    const getPassword = () => String(formContext.values['newPassword'] ?? '')
    const getCheck = () => checkPassword(getPassword(), String(formContext.values['confirmPassword'] ?? ''))

    const buildRules = (check: ReturnType<typeof checkPassword>): PasswordRule[] => [
        { key: 'length', done: check.lengthOk, label: `Мінімум ${PASSWORD_MIN_LENGTH_LABEL}` },
        {
            key: 'match',
            done: check.matchOk,
            failed: check.mismatch,
            label: 'Паролі збігаються',
            failedLabel: 'Паролі не збігаються',
        },
    ]

    const generate = () => {
        const next = generatePassword()
        formContext.setValue('newPassword', next)
        formContext.setValue('confirmPassword', next)
        formContext.resetErrors()
        shown.set(next, null)
    }

    // Пароль у полі збігається зі згенерованим — або поля порожні після збереження згенерованого
    const getShownPassword = () => {
        const password = getPassword()
        return password !== '' ? (password === shown.generated ? shown.generated : null) : shown.saved
    }

    const copy = async (value: string) => {
        try {
            if (!navigator.clipboard) throw new Error('clipboard unavailable')
            await navigator.clipboard.writeText(value)
            showSuccess('Скопійовано')
        } catch {
            showError('Не вдалося скопіювати')
        }
    }

    return (
        <Form
            formContext={formContext}
            onSubmit={() => {
                if (getCheck().canSubmit && !store.loading) void formContext.submit()
            }}
            isLanguageSwitcher={false}
        >
            <FormSection title="Змінити пароль" hint="Введіть новий пароль двічі або згенеруйте випадковий.">
                <FormRow>
                    <FormField name="newPassword" />
                    <FormField name="confirmPassword" />
                </FormRow>
                <Observer>
                    {() => {
                        const check = getCheck()
                        const shownPassword = getShownPassword()
                        return (
                            <>
                                <PasswordRules rules={buildRules(check)} />
                                {shownPassword && (
                                    <div className={cx(styles, 'generated')} role="status">
                                        <span>
                                            {getPassword() !== '' ? 'Згенерований пароль' : 'Збережений пароль'}:
                                        </span>
                                        <span className={cx(styles, 'mono')}>{shownPassword}</span>
                                        <button
                                            type="button"
                                            className={cx(styles, 'copy')}
                                            aria-label="Скопіювати пароль"
                                            title="Скопіювати"
                                            onClick={() => void copy(shownPassword)}
                                        >
                                            <TbCopy aria-hidden="true" />
                                        </button>
                                    </div>
                                )}
                                <div className={cx(styles, 'actions')}>
                                    <Button onClick={generate} disabled={store.loading}>
                                        Згенерувати
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        disabled={!check.canSubmit}
                                        loading={store.loading}
                                    >
                                        Зберегти пароль
                                    </Button>
                                </div>
                            </>
                        )
                    }}
                </Observer>
            </FormSection>
        </Form>
    )
})
